import { passes, type ColumnSelection } from './columnFilters'

/**
 * The daily roster (docs/product/reports.md): the officers on duty on one
 * date, by unit, shift, and title, with each officer's assignment. A unit is
 * a team. Pure, so the screen and the exports read one result.
 */
export type DailyRosterEntry = {
  employeeId: string
  identifier: string
  employee: string
  teamId: string
  statusCategory: string
  // The shift the entry was scheduled on, or null when it names none.
  shift: string | null
  startTime: string | null
  endTime: string | null
  assignment: string | null
}

export type DailyRosterPlacement = { title: string; shift: string | null }

export type DailyRosterRow = {
  key: string
  employeeId: string
  identifier: string
  employee: string
  teamId: string
  unit: string
  shift: string
  title: string
  assignment: string
}

// The values each column is narrowed to; a unit is named by its team id, a name by
// the employee id.
export type DailyRosterFilters = {
  unit?: ColumnSelection
  shift?: ColumnSelection
  title?: ColumnSelection
  employee?: ColumnSelection
  assignment?: ColumnSelection
}

export const NO_SHIFT = 'No shift'
export const NO_TITLE = 'No title'
export const NO_ASSIGNMENT = 'Unassigned'

const clock = (time: string) => time.slice(0, 5).replace(':', '')

// The shift an officer worked: the one the entry names, else the one their
// placement carries, else the hours worked, else none.
function shiftFor(entry: DailyRosterEntry, placement: DailyRosterPlacement | undefined): string {
  if (entry.shift) return entry.shift
  if (placement?.shift) return placement.shift
  if (entry.startTime && entry.endTime) return `${clock(entry.startTime)}-${clock(entry.endTime)}`
  return NO_SHIFT
}

/**
 * Everyone on duty on the date: a work entry, less anyone whose full-day
 * leave was approved, the same rule the daily assignment sheet applies. Ordered
 * by unit, shift, title, then name.
 */
export function buildDailyRoster(
  entries: DailyRosterEntry[],
  placements: Map<string, DailyRosterPlacement>,
  onApprovedLeave: Set<string>,
  teams: Map<string, string>,
): DailyRosterRow[] {
  return entries
    .filter((entry) => entry.statusCategory === 'work' && !onApprovedLeave.has(entry.employeeId))
    .map((entry, index): DailyRosterRow => {
      const placement = placements.get(entry.employeeId)
      return {
        key: `${entry.employeeId}|${index}`,
        employeeId: entry.employeeId,
        identifier: entry.identifier,
        employee: entry.employee,
        teamId: entry.teamId,
        unit: teams.get(entry.teamId) ?? 'Unknown unit',
        shift: shiftFor(entry, placement),
        title: placement?.title || NO_TITLE,
        assignment: entry.assignment ?? NO_ASSIGNMENT,
      }
    })
    .sort(
      (a, b) =>
        a.unit.localeCompare(b.unit) ||
        a.shift.localeCompare(b.shift) ||
        a.title.localeCompare(b.title) ||
        a.employee.localeCompare(b.employee) ||
        a.identifier.localeCompare(b.identifier),
    )
}

export function filterDailyRoster(rows: DailyRosterRow[], filters: DailyRosterFilters): DailyRosterRow[] {
  return rows.filter(
    (row) =>
      passes(filters.unit, row.teamId) &&
      passes(filters.shift, row.shift) &&
      passes(filters.title, row.title) &&
      passes(filters.assignment, row.assignment) &&
      passes(filters.employee, row.employeeId),
  )
}
